import { FormProvider, UseFormReturn } from 'react-hook-form';
import { ScoreContextProvider } from '../context/score.provider';
import { Editor, Frame, NodeId, Resolver, SerializedNodes, useEditor } from '@craftjs/core';
import { FC, useLayoutEffect, useState } from 'react';
import { AttachmentsContextProvider } from '../context/attachments.provider';
import { DataType } from 'src/components/formEditor/uiEditor/uiEditor';
import { IBaseScoreSection, IBaseVariableSection } from 'src/services/api/api-client';
import { LayoutAttachmentType } from '../context/attachments.context';

export type GeneralFormAnswerProps = {
  form: UseFormReturn<DataType, any, undefined>;
  resolver: Resolver;
  defaultLayout: SerializedNodes;
  initialScoreSections: IBaseScoreSection[];
  initialVariables: IBaseVariableSection[];
  initialAnswers?: any;
  initialAttachments?: Record<NodeId, LayoutAttachmentType>;
};

export const GeneralFormAnswer: FC<GeneralFormAnswerProps> = ({
  initialScoreSections,
  initialVariables,
  resolver,
  defaultLayout,
  initialAttachments,
  form,
}) => {
  return (
    <FormProvider {...form}>
      <ScoreContextProvider initialScoreSections={initialScoreSections} initialVariables={initialVariables}>
        <Editor resolver={resolver} enabled={false}>
          <AttachmentsContextProvider uploadFileFunction={undefined} initialAttachments={initialAttachments}>
            <FrameWithDeserialize data={defaultLayout} />
          </AttachmentsContextProvider>
        </Editor>
      </ScoreContextProvider>
    </FormProvider>
  );
};

// Frame stores `data` in useRef and only deserializes on the initial render (via useEffect).
// In @react-pdf/renderer useEffect does NOT fire (passive effects are never flushed).
// Workaround: deserialize data via useLayoutEffect (which fires synchronously during commit),
// then mount Frame on the second render pass when the Editor store is already populated.
const FrameWithDeserialize: FC<{ data: SerializedNodes }> = ({ data }) => {
  const { actions } = useEditor();
  const [deserialized, setDeserialized] = useState(false);

  useLayoutEffect(() => {
    actions.deserialize(data);
    setDeserialized(true);
  }, [actions, data]);

  if (!deserialized) return null;
  return <Frame data={data} />;
};
